import {InfoIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {useCallback, useRef} from 'react'

import {useThemedMediaUrl} from '../../helpers/media-urls'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useColumnWarning} from '../../hooks/use-column-warning'
import {useAllColumns} from '../../state-providers/columns/use-all-columns'
import {useUserNotices} from '../../state-providers/user-notices/user-notices-provider'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {UserNoticeResources} from '../../strings'
import {RenameProjectFieldDialog} from '../rename-project-field-dialog'
import styles from './memex-issue-type-rename-popover.module.css'
import {UserNoticePopover} from './user-notice-popover'

export const MemexIssueTypeRenamePopover = ({anchorRef}: {anchorRef: React.RefObject<HTMLElement | null>}) => {
  const {hasAdminPermissions} = ViewerPrivileges()
  const popoverImage = useThemedMediaUrl('issueTypes', 'popover')

  const {allColumns} = useAllColumns()
  const {userNotices, dismissUserNotice, setUserNoticeVariant, userNoticeVariants} = useUserNotices()
  const getStartedButtonRef = useRef<HTMLButtonElement | null>(null)
  const {anchorRef: triggerRef} = useViewOptionsMenuRef()

  const userDefinedTypeColumn = allColumns.find(
    column => column.name.toLowerCase().trim() === 'type' && column.userDefined === true,
  )

  const columnWarning = useColumnWarning(userDefinedTypeColumn)
  const onDismiss = useCallback(
    (tookAction = false) => {
      dismissUserNotice('memex_issue_types_rename_prompt', tookAction)
      triggerRef.current?.focus()
    },
    [dismissUserNotice, triggerRef],
  )

  const onGetStarted = useCallback(() => {
    setUserNoticeVariant('memex_issue_types_rename_prompt', 'modal')
  }, [setUserNoticeVariant])

  const onRenameDialogCancel = useCallback(() => {
    setUserNoticeVariant('memex_issue_types_rename_prompt', 'unset')
    setTimeout(() => getStartedButtonRef?.current?.focus(), 250)
  }, [setUserNoticeVariant])

  // memex_issue_types_rename_prompt notice should not be returned in JSON island if these conditions are false,
  // however, this check is included for added safety.
  if (!userDefinedTypeColumn || !hasAdminPermissions) return null
  if (!userNotices.memex_issue_types_rename_prompt) return null
  if (userDefinedTypeColumn == null || columnWarning !== 'rename-custom-type-column') return null

  const isRenameDialogOpen = userNoticeVariants.memex_issue_types_rename_prompt === 'modal'

  return (
    <>
      {isRenameDialogOpen ? (
        <RenameProjectFieldDialog
          column={userDefinedTypeColumn}
          onCancel={onRenameDialogCancel}
          onConfirm={() => onDismiss(true)}
          variant="issue-type"
        />
      ) : (
        <UserNoticePopover anchorRef={anchorRef} imageToLoad={popoverImage}>
          <Heading as="h2" className={styles.popoverHeading}>
            <div className={styles.headingRow}>
              <InfoIcon size={14} className={styles.infoIcon} />
              {UserNoticeResources.IssueTypeRenamePopover.title}
            </div>
          </Heading>
          <p className={styles.popoverText}>
            {UserNoticeResources.IssueTypeRenamePopover.description(userDefinedTypeColumn.name)}
          </p>
          <img
            src={popoverImage}
            alt={UserNoticeResources.IssueTypeRenamePopover.altText}
            className={styles.popoverImage}
          />
          <div className={styles.actionsRow}>
            <Button ref={getStartedButtonRef} onClick={onGetStarted}>
              {UserNoticeResources.IssueTypeRenamePopover.actionPrimary}
            </Button>
            <Button variant="invisible" onClick={() => onDismiss()}>
              {UserNoticeResources.IssueTypeRenamePopover.actionSecondary}
            </Button>
          </div>
        </UserNoticePopover>
      )}
    </>
  )
}
